import { isShootingSessionActive } from '@/lib/shootingGuideResume';

export const PAGE_LANGUAGE = 'fr';

export type AppLanguageCode = 'fr' | 'en' | 'es' | 'it' | 'ar' | 'zh-CN' | 'pt';

export type AppLanguage = {
  code: AppLanguageCode;
  label: string;
  /** Code ISO pays pour l’image drapeau (desktop) */
  flagIso: string;
  mobileCode: string;
};

export const APP_LANGUAGES: AppLanguage[] = [
  { code: 'fr', label: 'Français', flagIso: 'fr', mobileCode: 'FR' },
  { code: 'en', label: 'Anglais', flagIso: 'gb', mobileCode: 'EN' },
  { code: 'es', label: 'Espagnol', flagIso: 'es', mobileCode: 'ES' },
  { code: 'it', label: 'Italien', flagIso: 'it', mobileCode: 'IT' },
  { code: 'ar', label: 'Arabe', flagIso: 'sa', mobileCode: 'AR' },
  { code: 'zh-CN', label: 'Chinois', flagIso: 'cn', mobileCode: 'CN' },
  { code: 'pt', label: 'Portugais', flagIso: 'pt', mobileCode: 'PT' },
];

/** Drapeau image (affichage fiable sur Windows / desktop). */
export function flagImageUrl(flagIso: string, width = 40): string {
  return `https://flagcdn.com/w${width}/${flagIso}.png`;
}

export const GOOGLE_INCLUDED_LANGUAGES = APP_LANGUAGES.map((l) => l.code).join(',');

export function readActiveLanguageFromCookie(): AppLanguageCode {
  if (typeof document === 'undefined') return 'fr';
  const match = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  const code = match?.[1];
  if (code && APP_LANGUAGES.some((l) => l.code === code)) {
    return code as AppLanguageCode;
  }
  return 'fr';
}

function clearGoogTransCookies(): void {
  const hostname = window.location.hostname;
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `googtrans=;expires=${expires};path=/`;
  document.cookie = `googtrans=;expires=${expires};path=/;domain=${hostname}`;
  document.cookie = `googtrans=;expires=${expires};path=/;domain=.${hostname}`;
}

export function applyGoogleTranslateLanguage(lang: AppLanguageCode): void {
  if (isShootingSessionActive()) {
    return;
  }
  clearGoogTransCookies();
  if (lang !== 'fr') {
    const value = `/${PAGE_LANGUAGE}/${lang}`;
    const hostname = window.location.hostname;
    document.cookie = `googtrans=${value};path=/`;
    document.cookie = `googtrans=${value};path=/;domain=${hostname}`;
    document.cookie = `googtrans=${value};path=/;domain=.${hostname}`;
  }

  const select = document.querySelector<HTMLSelectElement>('.goog-te-combo');
  if (select) {
    select.value = lang;
    select.dispatchEvent(new Event('change'));
    return;
  }

  window.location.reload();
}

export function isGoogleTranslateActive(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  if (root.classList.contains('translated-ltr') || root.classList.contains('translated-rtl')) {
    return true;
  }
  return readActiveLanguageFromCookie() !== 'fr';
}

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate: {
        TranslateElement: new (
          options: Record<string, string>,
          elementId: string
        ) => void;
      };
    };
  }
}
